import React from 'react';
import { Text, GridFlex, Form, SearchableSelect, FormRenderProps, SearchableSelectOption } from '@stigg-components';
import { Currency, CustomerResponseFragment, VendorIdentifier } from '@stigg-types/apiTypes';
import { getCurrencyText } from '../../../../packages/pricing/components/currency/currencyUtils';
import { t } from 'i18next';
import { useIntegrations } from '@stigg-common';
import { BillingProviderPicker } from './BillingProviderPicker';
import { SuspendedPaymentMethod } from './SuspendedPaymentMethod';

export interface AddPaymentMethodFormValues {
  billingProvider: string | undefined;
  billingCurrency: Currency | undefined;
}

export type AddPaymentMethodProps = {
  customer: CustomerResponseFragment;
  onSuccess: () => void;
  onCancel: () => void;
  addButtonText?: string;
  cancelButtonText?: string;
  additionalCustomControls?: React.ReactNode;
  additionalCustomContent?: React.ReactNode;
  integrationId?: string;
};

function renderCurrencySelector(
  formProps: FormRenderProps<AddPaymentMethodFormValues>,
  currencyOptions: SearchableSelectOption[],
  isZuora: boolean,
) {
  if (!isZuora) {
    return null;
  }

  return (
    <GridFlex.Column mb={3} mt={3}>
      <SearchableSelect
        selectedOptionId={formProps.values.billingCurrency}
        onChange={(id) => formProps.setFieldValue('billingCurrency', id)}
        label={t('pricing.priceLocalization.currency')}
        placeholder={t('pricing.priceLocalization.selectCurrency')}
        options={currencyOptions}
        dataTestId="currency-select"
        maxOptionsToShow={20}
      />
    </GridFlex.Column>
  );
}

export function AddPaymentMethod({
  customer,
  onSuccess,
  onCancel,
  addButtonText,
  cancelButtonText,
  additionalCustomControls,
  integrationId,
}: AddPaymentMethodProps) {
  const { hasMultipleBillingIntegrations, getCurrentBillingIntegration } = useIntegrations();

  const currencyOptions = Object.values(Currency).map((currencyCode) => ({
    id: currencyCode,
    render: () => <Text.B2>{getCurrencyText(currencyCode)}</Text.B2>,
    isInSearch: (search: string) =>
      getCurrencyText(currencyCode).toLowerCase().includes(search.toLowerCase()) ||
      currencyCode.toLowerCase().includes(search.toLowerCase()),
  }));

  const initialValues: AddPaymentMethodFormValues = {
    billingProvider: integrationId || undefined,
    billingCurrency: currencyOptions.find((c) => c.id === Currency.Usd)?.id,
  };

  return (
    <GridFlex.Column sx={{ height: '100%', width: '100%' }} overflow="hidden">
      <Text.H3 mt={1} mb={2}>
        {t('customers.paymentMethod.addPaymentMethod')}
      </Text.H3>
      <Form
        hideSaveButton
        withStickyFooter
        footerSx={{ height: 0, padding: 0 }}
        initialValues={initialValues}
        onSubmit={() => {}}
        fields={[
          {
            type: 'select',
            id: 'billingProvider',
            hide: () => !hasMultipleBillingIntegrations || !!integrationId,
            gridProps: {
              mb: 2,
            },
            label: t('customers.paymentMethod.selectBillingProvider'),
            values: BillingProviderPicker(),
          },
          {
            type: 'custom',
            hide: (values: AddPaymentMethodFormValues) => hasMultipleBillingIntegrations && !values.billingProvider,
            render: (props) => {
              const selectedIntegration = getCurrentBillingIntegration(props.values.billingProvider);
              const isZuora = selectedIntegration?.vendorIdentifier === VendorIdentifier.Zuora;

              const currencySelectorControl = renderCurrencySelector(props, currencyOptions, isZuora);

              return (
                <SuspendedPaymentMethod
                  customer={customer}
                  onSuccess={onSuccess}
                  onCancel={onCancel}
                  addButtonText={addButtonText}
                  cancelButtonText={cancelButtonText}
                  additionalCustomContent={currencySelectorControl}
                  additionalCustomControls={additionalCustomControls}
                  formRenderProps={props}
                />
              );
            },
          },
        ]}
      />
    </GridFlex.Column>
  );
}
